// src/features/whatsapp/pages/WhatsAppPage.tsx
// ============================================================================
// CONEXIÓN WHATSAPP — re-cableada a lealify-base (Inc11b)
// ============================================================================
// El backend (lealify-base) es el BROKER de la sesión WAHA del negocio:
//   manager → /api/messages/whatsapp/* → WAHA (llave maestra server-side).
// Sin proxy PHP ni Firebase. Modelo "WhatsApp bajo solicitud": el negocio tiene
// UNA sesión, aprovisionada por la plataforma (proxy dedicado anti-ban). El dueño
// solo vincula/reinicia/desvincula SU sesión escaneando el QR.
//
// Estados del broker base (/messages/whatsapp/{status,qr,start,restart,logout}):
//   { configured, connected, status, me, qr?, retry? }
//   - configured=false → WhatsApp aún no aprovisionado para este negocio.
//   - status: WORKING | SCAN_QR_CODE | STARTING | STOPPED | FAILED | ERROR | ...
//
// Se eliminó (vs versión Firebase): multi-sesión, config de proxy, asignación de
// sucursales y rate-limit. El "mensaje de prueba" SÍ se conserva (no descuenta cupo);
// el envío masivo real vive en campañas/mensajes por evento, server-side anti-ban.
//
// Ruta: /dashboard/whatsapp · Acceso: PRO con WhatsApp provisionado.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { canUseWhatsapp, getWhatsappAccessState } from '../../../utils/whatsappAccess';
import WhatsAppRequestPanel from '../../../components/whatsapp/WhatsAppRequestPanel';
import { ProLocked } from '../../../components/pro/ProLocked';
import { MessageSquare, QrCode, Wifi, WifiOff, RefreshCw, LogOut, Loader2, AlertCircle, CheckCircle, Smartphone, Clock, Info } from 'lucide-react';

import { whatsappApi, type WhatsAppStatus, type WaHealth } from '../../../api/whatsappApi';
import { acceptWhatsAppDisclaimer } from '../../../services/whatsappDisclaimerService';
import WhatsAppDisclaimerModal from '../components/WhatsAppDisclaimerModal';
import WhatsAppHealthCard from '../components/WhatsAppHealthCard';
import WaPauseCard from '../components/WaPauseCard';

// ── Constantes de polling ──
const STATUS_POLL_INTERVAL = 5000;   // refresca estado cuando está iniciando
const QR_POLL_INTERVAL = 4000;       // pide QR / detecta conexión al escanear
const QR_MAX_POLL_TIME = 120000;     // 2 min antes de pedir refresh manual

const WhatsAppPage: React.FC = () => {
  const { currentUser } = useAuth();

  // ── Acceso (PRO + WhatsApp provisionado) ──
  const hasPremiumAccess = canUseWhatsapp(currentUser);
  const waAccessState = getWhatsappAccessState(currentUser);

  // ── Estado de la sesión (broker base) ──
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // ── QR ──
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [qrExpired, setQrExpired] = useState(false);

  // ── Mensajes ──
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ── Cupo de la hora (X de 30 restantes) ──
  const [quota, setQuota] = useState<{ cap: number; used: number; remaining: number } | null>(null);

  // ── Salud del número (foto diaria del cron wa-health) ──
  const [health, setHealth] = useState<WaHealth | null>(null);

  // ── Probar conexión (mensaje de prueba) ──
  const [testPhone, setTestPhone] = useState('');
  const [testMsg, setTestMsg] = useState('¡Hola! Este es un mensaje de prueba desde Lealify. 🎉');
  const [testSending, setTestSending] = useState(false);

  // ── Refs de polling ──
  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrStartTimeRef = useRef<number>(0);

  // ── Descargo de responsabilidad ──
  const [disclaimerAcceptedAt, setDisclaimerAcceptedAt] = useState<Date | null>(
    toDate(currentUser?.whatsappDisclaimer?.accepted ? currentUser?.whatsappDisclaimer?.acceptedAt : null)
  );
  const disclaimerAccepted = !!currentUser?.whatsappDisclaimer?.accepted || !!disclaimerAcceptedAt;
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [acceptingDisclaimer, setAcceptingDisclaimer] = useState(false);
  const pendingConnectRef = useRef<(() => void) | null>(null);

  const clearAllPolling = useCallback(() => {
    if (statusPollRef.current) { clearInterval(statusPollRef.current); statusPollRef.current = null; }
    if (qrPollRef.current) { clearInterval(qrPollRef.current); qrPollRef.current = null; }
  }, []);

  useEffect(() => () => clearAllPolling(), [clearAllPolling]);

  // ── Compuerta del descargo ──
  const requireDisclaimer = useCallback((action: () => void) => {
    if (disclaimerAccepted) action();
    else { pendingConnectRef.current = action; setShowDisclaimer(true); }
  }, [disclaimerAccepted]);

  const handleAcceptDisclaimer = useCallback(async () => {
    if (!currentUser?.uid) return;
    setAcceptingDisclaimer(true);
    setError(null);
    const result = await acceptWhatsAppDisclaimer(currentUser.uid);
    setAcceptingDisclaimer(false);
    if (!result.success) {
      setError(result.error || 'No se pudo registrar la aceptación. Intenta de nuevo.');
      return;
    }
    setDisclaimerAcceptedAt(new Date());
    setShowDisclaimer(false);
    const pending = pendingConnectRef.current;
    pendingConnectRef.current = null;
    if (pending) pending();
  }, [currentUser?.uid]);

  const handleCancelDisclaimer = useCallback(() => {
    pendingConnectRef.current = null;
    setShowDisclaimer(false);
  }, []);

  // ── Cargar estado ──
  const loadStatus = useCallback(async () => {
    try {
      const result = await whatsappApi.status();
      setStatus(result);
      setError(result.detail && result.status === 'ERROR' ? 'No se pudo contactar el servicio de WhatsApp.' : null);
      if (result.connected) { setShowQR(false); setQrCode(null); }
      return result;
    } catch (err: any) {
      setError(err?.message || 'Error al verificar el estado de WhatsApp.');
      return null;
    }
  }, []);

  useEffect(() => {
    if (!hasPremiumAccess) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      await loadStatus();
      setLoading(false);
    })();
  }, [hasPremiumAccess, loadStatus]);

  // Carga el cupo de la hora cuando la sesión está conectada (refresca tras enviar prueba).
  const loadQuota = useCallback(async () => {
    try { setQuota(await whatsappApi.quota()); } catch { /* no crítico */ }
  }, []);
  useEffect(() => { if (status?.connected) loadQuota(); }, [status?.connected, loadQuota]);

  // Salud del número: se carga una vez si el negocio tiene WhatsApp aprovisionado.
  useEffect(() => {
    if (!hasPremiumAccess || !status?.configured) return;
    whatsappApi.health().then(setHealth).catch(() => { /* no crítico */ });
  }, [hasPremiumAccess, status?.configured]);

  // Enviar mensaje de prueba a un número arbitrario (confirma que la sesión jala).
  const handleSendTest = useCallback(async () => {
    const digits = testPhone.replace(/\D/g, '');
    if (digits.length < 10) { setError('Ingresa los 10 dígitos del número.'); return; }
    setTestSending(true); setError(null); setSuccessMessage(null);
    try {
      const r = await whatsappApi.sendTest(digits, testMsg);
      if (r.ok) { setSuccessMessage(`Mensaje de prueba enviado a ${digits}.`); loadQuota(); }
      else setError(r.shadow ? 'WhatsApp aún no está aprovisionado para tu negocio.' : `No se pudo enviar: ${r.detail || 'revisa el número'}.`);
    } catch (e: any) {
      setError(e?.message || 'No se pudo enviar el mensaje de prueba.');
    } finally { setTestSending(false); }
  }, [testPhone, testMsg, loadQuota]);

  // Auto-abrir descargo para negocios YA conectados que nunca pasan por "Vincular".
  useEffect(() => {
    if (hasPremiumAccess && !loading && status?.configured && !disclaimerAccepted) {
      setShowDisclaimer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPremiumAccess, loading, status?.configured, disclaimerAccepted]);

  // Polling de estado mientras la sesión está iniciando (sin QR a la vista).
  useEffect(() => {
    if (!status || showQR) return;
    if (status.status === 'STARTING') {
      statusPollRef.current = setInterval(async () => {
        const r = await loadStatus();
        if (r?.connected) {
          clearAllPolling();
          setSuccessMessage('¡WhatsApp conectado exitosamente!');
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      }, STATUS_POLL_INTERVAL);
    }
    return () => {
      if (statusPollRef.current) { clearInterval(statusPollRef.current); statusPollRef.current = null; }
    };
  }, [status?.status, showQR, loadStatus, clearAllPolling]);

  // ── Polling de QR (pide QR y detecta la conexión al escanear) ──
  const startQrPolling = useCallback(() => {
    if (qrPollRef.current) clearInterval(qrPollRef.current);
    qrStartTimeRef.current = Date.now();
    setQrExpired(false);

    const tick = async () => {
      if (Date.now() - qrStartTimeRef.current > QR_MAX_POLL_TIME) {
        clearAllPolling();
        setQrExpired(true);
        return;
      }
      try {
        const r = await whatsappApi.qr();
        if (r.connected) {
          clearAllPolling();
          setShowQR(false);
          setQrCode(null);
          setSuccessMessage('¡WhatsApp conectado exitosamente!');
          setTimeout(() => setSuccessMessage(null), 5000);
          await loadStatus();
        } else if (r.qr) {
          setQrCode(r.qr);
        }
        // r.retry → la sesión aún madura; seguimos en el próximo tick.
      } catch { /* reintenta en el próximo tick */ }
    };

    tick();
    qrPollRef.current = setInterval(tick, QR_POLL_INTERVAL);
  }, [clearAllPolling, loadStatus]);

  // ── Handlers ──
  const handleConnect = () => requireDisclaimer(async () => {
    setActionLoading('connect');
    setError(null);
    setQrCode(null);
    setShowQR(true);
    try {
      await whatsappApi.start();
      startQrPolling();
    } catch (err: any) {
      setError(err?.message || 'No se pudo iniciar la conexión.');
      setShowQR(false);
    } finally {
      setActionLoading(null);
    }
  });

  const handleRefreshQr = () => { setQrExpired(false); startQrPolling(); };

  const handleRestart = async () => {
    setActionLoading('restart');
    setError(null);
    clearAllPolling();
    try {
      await whatsappApi.restart();
      setShowQR(false);
      setQrCode(null);
      await loadStatus();
      setSuccessMessage('Sesión reiniciada. Si pide QR, vuelve a vincular.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'No se pudo reiniciar la sesión.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = async () => {
    setActionLoading('logout');
    setError(null);
    clearAllPolling();
    try {
      await whatsappApi.logout();
      setShowQR(false);
      setQrCode(null);
      await loadStatus();
      setSuccessMessage('WhatsApp desvinculado.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'No se pudo desvincular.');
    } finally {
      setActionLoading(null);
    }
  };

  // ========================================================================
  // GUARDS
  // ========================================================================
  if (!hasPremiumAccess) {
    return (
      <Layout>
        {waAccessState === 'free' ? (
          <div className="space-y-6 px-2 sm:px-0">
            <div className="animate-fade-in-up">
              <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center"><Smartphone className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-emerald-500" />WhatsApp del negocio</h1>
              <p className="mt-1 text-sm text-content-2">Conecta el WhatsApp de tu negocio para enviar los mensajes desde tu número</p>
            </div>
            <ProLocked feature="whatsapp" />
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <WhatsAppRequestPanel state={waAccessState} feature="WhatsApp del negocio" />
          </div>
        )}
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-green-500" />
          <span className="ml-3 text-content-2">Verificando conexión de WhatsApp...</span>
        </div>
      </Layout>
    );
  }

  // WhatsApp aún no aprovisionado para este negocio (modelo "bajo solicitud").
  if (status && status.configured === false) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-surface-1 border border-edge rounded-2xl p-8 text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
              <Clock className="w-7 h-7 text-green-500" />
            </div>
            <h1 className="text-xl font-bold text-content-1">Tu WhatsApp del negocio está en activación</h1>
            <p className="text-content-2 max-w-md mx-auto">
              Activamos WhatsApp <strong>bajo solicitud</strong> con un número y una conexión dedicada
              para proteger tu cuenta. En cuanto esté lista, aquí aparecerá el código QR para vincular tu teléfono.
            </p>
            <p className="text-sm text-content-3">
              ¿Ya lo solicitaste y no aparece? Escríbenos y lo revisamos.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // ========================================================================
  // RENDER PRINCIPAL
  // ========================================================================
  const isConnected = status?.connected ?? false;
  const statusLabel = getStatusLabel(status?.status ?? 'UNKNOWN');
  const phone = status?.me?.id ? String(status.me.id).split('@')[0] : null;

  return (
    <Layout>
      <div className="w-full space-y-6">
        {/* Encabezado */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-green-500/10 flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-content-1">WhatsApp del negocio</h1>
            <p className="text-sm text-content-2">Vincula tu teléfono para enviar mensajes a tus clientes</p>
          </div>
        </div>

        {/* Avisos */}
        {error && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-600 rounded-xl p-3 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}
        {successMessage && (
          <div className="flex items-start gap-2 bg-green-500/10 border border-green-500/30 text-green-600 rounded-xl p-3 text-sm">
            <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" /> <span>{successMessage}</span>
          </div>
        )}

        {/* Tarjeta de estado */}
        <div className="bg-surface-1 border border-edge rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isConnected
                ? <Wifi className="w-6 h-6 text-green-500" />
                : <WifiOff className="w-6 h-6 text-content-3" />}
              <div>
                <p className="font-semibold text-content-1">{statusLabel}</p>
                {phone
                  ? <p className="text-sm text-content-2 flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> +{phone}</p>
                  : <p className="text-sm text-content-3">Sin vincular</p>}
              </div>
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              isConnected ? 'bg-green-500/15 text-green-600' : 'bg-surface-2 text-content-3'
            }`}>
              {isConnected ? 'Conectado' : 'Desconectado'}
            </span>
          </div>

          {/* Acciones cuando NO está conectado */}
          {!isConnected && !showQR && (
            <button
              onClick={handleConnect}
              disabled={actionLoading === 'connect'}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-semibold rounded-xl py-3 transition-colors"
            >
              {actionLoading === 'connect'
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Generando QR...</>
                : <><QrCode className="w-5 h-5" /> Vincular WhatsApp</>}
            </button>
          )}

          {/* Cupo de la hora (X de 30 restantes) */}
          {isConnected && quota && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-content-2 mb-1.5">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {quota.remaining} de {quota.cap} mensajes restantes esta hora</span>
              </div>
              <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
                <div className="h-full rounded-full bg-green-500" style={{ width: `${Math.round((quota.remaining / (quota.cap || 30)) * 100)}%` }} />
              </div>
            </div>
          )}

          {/* Acciones cuando SÍ está conectado */}
          {isConnected && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={handleRestart}
                disabled={!!actionLoading}
                className="flex items-center justify-center gap-2 bg-surface-2 hover:bg-surface-3 disabled:opacity-60 text-content-1 font-medium rounded-xl py-2.5 transition-colors"
              >
                {actionLoading === 'restart' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Reiniciar
              </button>
              <button
                onClick={handleLogout}
                disabled={!!actionLoading}
                className="flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-60 text-red-600 font-medium rounded-xl py-2.5 transition-colors"
              >
                {actionLoading === 'logout' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                Desvincular
              </button>
            </div>
          )}
        </div>

        {/* Salud del número (score diario 0-100 + semáforo) */}
        {health && <WhatsAppHealthCard health={health} />}

        {/* Pausa de mensajes automáticos — la alternativa a desvincular (9-sep-2026) */}
        <WaPauseCard />

        {/* Panel de QR */}
        {showQR && !isConnected && (
          <div className="bg-surface-1 border border-edge rounded-2xl p-6 text-center space-y-4">
            <h2 className="font-semibold text-content-1">Escanea el código con WhatsApp</h2>
            <ol className="text-sm text-content-2 text-left max-w-xs mx-auto space-y-1 list-decimal list-inside">
              <li>Abre WhatsApp en tu teléfono</li>
              <li>Toca <strong>Ajustes → Dispositivos vinculados</strong></li>
              <li>Toca <strong>Vincular un dispositivo</strong> y escanea</li>
            </ol>

            <div className="flex justify-center">
              {qrCode ? (
                <img src={qrCode} alt="Código QR de WhatsApp" className="w-56 h-56 rounded-xl border border-edge bg-white p-2" />
              ) : qrExpired ? (
                <div className="w-56 h-56 rounded-xl border border-edge flex flex-col items-center justify-center text-content-3 gap-2">
                  <Clock className="w-8 h-8" />
                  <span className="text-sm">El QR expiró</span>
                </div>
              ) : (
                <div className="w-56 h-56 rounded-xl border border-edge flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                </div>
              )}
            </div>

            {qrExpired ? (
              <button onClick={handleRefreshQr} className="inline-flex items-center gap-2 text-green-600 font-medium">
                <RefreshCw className="w-4 h-4" /> Generar un nuevo QR
              </button>
            ) : (
              <p className="text-xs text-content-3 flex items-center justify-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Esperando a que escanees...
              </p>
            )}

            <button
              onClick={() => { clearAllPolling(); setShowQR(false); setQrCode(null); }}
              className="text-sm text-content-3 hover:text-content-1"
            >
              Cancelar
            </button>
          </div>
        )}

        {/* Probar conexión (mensaje de prueba) */}
        {isConnected && (
          <div className="bg-surface-1 border border-edge rounded-2xl p-5 space-y-3">
            <div>
              <h2 className="font-semibold text-content-1 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-green-500" /> Probar conexión</h2>
              <p className="text-sm text-content-2">Envía un mensaje de prueba para verificar que todo funciona correctamente.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-content-2 mb-1">Número de WhatsApp</label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-content-3 bg-surface-2 rounded-lg px-2.5 py-2">MX</span>
                <input
                  type="tel" inputMode="numeric" value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)} placeholder="6861234567"
                  className="flex-1 bg-surface-2 border border-edge rounded-lg px-3 py-2 text-sm text-content-1"
                />
              </div>
              <p className="text-[11px] text-content-3 mt-1">Solo ingresa los 10 dígitos del número (sin 52, sin 1). Ejemplo: 6861234567</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-content-2 mb-1">Mensaje</label>
              <textarea
                rows={2} value={testMsg} maxLength={500}
                onChange={(e) => setTestMsg(e.target.value)}
                className="w-full bg-surface-2 border border-edge rounded-lg px-3 py-2 text-sm text-content-1"
              />
              <p className="text-[11px] text-content-3 text-right">{testMsg.length}/500</p>
            </div>
            <button
              onClick={handleSendTest} disabled={testSending}
              className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-semibold rounded-xl py-2.5 transition-colors"
            >
              {testSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><MessageSquare className="w-4 h-4" /> Enviar mensaje de prueba</>}
            </button>
          </div>
        )}

        {/* Guía anti-ban */}
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-2xl p-5 space-y-2">
          <h2 className="font-semibold text-amber-700 dark:text-amber-500 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> Importante: Evita el ban de tu número
          </h2>
          <p className="text-sm text-content-2">
            WhatsApp puede suspender temporal o permanentemente los números que detecte enviando mensajes
            automatizados de forma abusiva. Para proteger tu cuenta:
          </p>
          <ul className="space-y-1.5 text-sm text-content-2">
            <li className="flex gap-2"><span className="text-amber-600">•</span><span><strong>No envíes spam.</strong> Solo contacta clientes que conocen tu negocio y esperan recibir tus mensajes.</span></li>
            <li className="flex gap-2"><span className="text-amber-600">•</span><span><strong>Respeta el límite de 30 mensajes por hora.</strong> El sistema aplica este límite automáticamente, no intentes evadirlo.</span></li>
            <li className="flex gap-2"><span className="text-amber-600">•</span><span><strong>No envíes mensajes idénticos</strong> a muchas personas. Personaliza tus campañas incluyendo el nombre del cliente.</span></li>
            <li className="flex gap-2"><span className="text-amber-600">•</span><span><strong>Usa un número dedicado para el negocio,</strong> no tu número personal. Si WhatsApp lo suspende, no perderás tu cuenta personal.</span></li>
            <li className="flex gap-2"><span className="text-amber-600">•</span><span><strong>Si un cliente te bloquea, no insistas.</strong> Los bloqueos frecuentes son una señal de alerta para WhatsApp.</span></li>
          </ul>
          <p className="text-xs text-content-3 pt-1">
            Lealify incluye protecciones automáticas (rate limiting, proxy anti-ban y delays entre mensajes),
            pero el uso responsable es tu mejor protección.
          </p>
        </div>

        {/* ¿Cómo funciona? */}
        <div className="bg-surface-1 border border-edge rounded-2xl p-5 space-y-2">
          <h2 className="font-semibold text-content-1 flex items-center gap-2"><Info className="w-5 h-5 text-blue-500" /> ¿Cómo funciona?</h2>
          <p className="text-sm text-content-2">
            Al vincular tu WhatsApp, podrás enviar mensajes directos a tus clientes desde las secciones de
            Cumpleaños, Campañas y Clientes Inactivos — sin necesidad de abrir WhatsApp Web.
          </p>
          <p className="text-sm text-content-2">
            Tu conexión está protegida y tiene un límite de 30 mensajes por hora para mantener tu cuenta segura.
          </p>
        </div>

        {disclaimerAcceptedAt && (
          <p className="text-center text-xs text-content-3 flex items-center justify-center gap-1">
            <Info className="w-3 h-3" /> Aceptaste el aviso de uso responsable el {fmtFecha(disclaimerAcceptedAt)}
          </p>
        )}
      </div>

      {showDisclaimer && (
        <WhatsAppDisclaimerModal
          onAccept={handleAcceptDisclaimer}
          onCancel={handleCancelDisclaimer}
          accepting={acceptingDisclaimer}
        />
      )}
    </Layout>
  );
};

// ============================================================================
// HELPERS
// ============================================================================
function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    'WORKING': 'Conectado',
    'SCAN_QR_CODE': 'Esperando escaneo de QR',
    'STARTING': 'Iniciando...',
    'STOPPED': 'Detenido',
    'STOPPING': 'Deteniendo...',
    'FAILED': 'Error de conexión',
    'ERROR': 'Servicio no disponible',
    'UNKNOWN': 'Desconocido',
  };
  return labels[status] || status;
}

/** Normaliza un valor de fecha (Firestore Timestamp | Date | epoch ms | string) a Date. */
function toDate(value: any): Date | null {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Formatea una fecha como DD/MM/AAAA. */
function fmtFecha(d: Date | null): string {
  if (!d) return '';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default WhatsAppPage;
